import React from 'react'
import {Stack} from '../../../vanilla'
import {useCompetitionMarketingFormFields} from '../../../hooks/forms/useCompetitionMarketingFormFields'
import {Field} from '../../field'
import {AddressDetails} from '../../../types/loqate'
import { PrivacyPolicyLink } from './privacy-policy-link'
import { AddressFieldWithHiddenInputs } from './address-field-with-hidden-inputs'
import { CheckboxesForCompetitionMarketingForm } from './checkboxes-for-competition-marketing-form'
import { StepFields } from './step-fields'
import { CompetitionMarketingFormFieldsProps } from '../../../types/cms/competition-marketing-form'
import { FieldWithSubText } from './field-with-subtext'

export const CompetitionMarketingFormFields = (props: CompetitionMarketingFormFieldsProps) => {
  const {questionnaire, name, phoneNumber, address, form, formCheckboxes, stepperMode, currentStep = 1, stepContent, stepConfig} = props

  const fields = useCompetitionMarketingFormFields({form, questionnaire})
  const showTermsAndConditionsLink = formCheckboxes?.displayTermsAndConditionsLink

  const applyAddressToFormMarketing = (address: AddressDetails) => {
    // Format the complete address for display as comma-separated components
    const addressParts = [
      address.Line1,
      address.Line2 && address.Line2.trim() ? address.Line2 : null,
      address.City,
      address.PostalCode,
    ]
      .filter(part => part && part.trim()) // Remove empty parts
      .map(part => part?.trim())
    
    const formattedAddress = addressParts.join(', ')
    form.setValue('address', formattedAddress)
    form.setValue('searchForAddress', address.PostalCode)
    form.clearErrors('address')

    // Also store individual components for form submission
    form.setValue('address1', address.Line1 || '')
    form.setValue('address2', address.Line2 || '')
    form.setValue('city', address.City || '')
    form.setValue('postalCode', address.PostalCode || '')
  }

  return (
    <Stack spacing="16px">
      {stepperMode ? (
        // Stepper mode - render fields based on current step and configuration
        <>
          {stepConfig?.steps.map((step, index) => {
            const stepNumber = index + 1
            if (currentStep !== stepNumber) return null
            
            return (
              <React.Fragment key={stepNumber}>
                {step.fields.map((fieldName, fieldIndex) => (
                  <React.Fragment key={fieldIndex}>
                    <StepFields
                      fieldName={fieldName}
                      fields={fields}
                      form={form}
                      formCheckboxes={formCheckboxes}
                      showTermsAndConditionsLink={showTermsAndConditionsLink}
                      applyAddressToFormMarketing={applyAddressToFormMarketing}
                      stepContent={stepContent}
                      subText={step.subText}
                      label={step.label}
                    />
                  </React.Fragment>
                ))}
              </React.Fragment>
            )
          })}
        </>
      ) : (
        // Regular mode - render all fields at once
        <>
            {questionnaire?.includeQuestionnaire && questionnaire?.question1 && (
              <>
                <FieldWithSubText
                  label={questionnaire?.question1}
                  subText={''}
                  hasError={!!form.formState.errors.question1}
                >
                  <Field {...fields.question1} type='textarea' showAsterisk inputGroupProps={{ style: { maxWidth: 450 } }} inputProps={{ style: { padding: '10px', resize: 'none' } }} />
                </FieldWithSubText>

                {questionnaire?.question2 && (
                  <>
                    <FieldWithSubText
                      label={questionnaire?.question2}
                      subText={''}
                      hasError={!!form.formState.errors.question2}
                    >
                      <Field {...fields.question2} type='textarea' showAsterisk inputGroupProps={{ style: { maxWidth: 450 } }} inputProps={{ style: { padding: '10px', resize: 'none' } }} />
                    </FieldWithSubText>

                    {questionnaire?.question3 && (
                      <FieldWithSubText
                        label={questionnaire?.question3}
                        subText={''}
                        hasError={!!form.formState.errors.question3}
                      >
                        <Field {...fields.question3} type='textarea' showAsterisk inputGroupProps={{ style: { maxWidth: 450 } }} inputProps={{ style: { padding: '10px', resize: 'none' } }} />
                      </FieldWithSubText>
                    )}
                  </>
                )}
              </>
            )}
          {name && (
            <>
              <Field {...fields.firstName} showAsterisk inputGroupProps={{style: {maxWidth: 450}}} />
              <FieldWithSubText
                subText={stepContent?.stepperFormSubtext?.nameFieldSubtext}
                hasError={!!form.formState.errors.lastname}
              >
                <Field {...fields.lastName} showAsterisk inputGroupProps={{style: {maxWidth: 450}}} />
              </FieldWithSubText>
            </>
          )}
          <Field {...fields.email} showAsterisk inputGroupProps={{style: {maxWidth: 450}}} />
          {phoneNumber && (
            <FieldWithSubText
              subText={stepContent?.stepperFormSubtext?.phoneFieldSubtext}
              hasError={!!form.formState.errors.phone}
            >
              <Field {...fields.phone} showAsterisk inputGroupProps={{style: {maxWidth: 450}}} />
            </FieldWithSubText>
          )}
          {address && (
            <AddressFieldWithHiddenInputs
              field={fields.address}
              form={form}
              applyAddressToFormMarketing={applyAddressToFormMarketing}
              hasError={!!form.formState.errors.address}
            />
          )}
          <CheckboxesForCompetitionMarketingForm
            form={form}
            formCheckboxes={formCheckboxes}
          />
          {showTermsAndConditionsLink && <PrivacyPolicyLink />}
        </>
      )}
    </Stack>
  )
}
